import { Bell, Blocks, Download, Puzzle, Search, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import {
  CODE_ENGINE,
  isBlobNode,
  isSecretPath,
  type CodeEngineCandidate,
  type CodeEngineExtension,
  type CodeEngineImport,
  type EngineTool,
  type ClaudeEmbedRequest,
  type CodeEngineStatus,
  type VsCodeInstall,
} from "@ide/shared";
import type { FileHandle } from "@/collab/session";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, Tip } from "@/components/ui/overlay";
import { Spinner } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import { cn } from "@/lib/utils";
import type { WorkspaceContextValue } from "./context";
import { activeWorkspace, deleteFile, readFile, writeFile } from "./file-ops";
import { recordAfter, recordBefore } from "@/assistant/ai-changes";
import { readProjectTexts } from "./git";

/**
 * Motor VS Code na página: instala o motor (VSCodium, baixado uma vez), traz todas as extensões
 * dos editores que a pessoa tem (VS Code, Cursor…), abre o projeto numa janela do motor e mantém
 * a pasta do motor e o projeto do time iguais enquanto a janela está aberta.
 */
const engine = () => desktop?.codeEngine ?? null;

interface EngineState {
  status: CodeEngineStatus | null;
  extensions: CodeEngineExtension[];
  /** Diálogo de instalação aberto. */
  dialog: boolean;
  /** Projeto que pediu o motor (abre nele depois de instalar). */
  target: WorkspaceContextValue | null;
  /** Projetos abertos no motor nesta janela. */
  open: string[];
  lastImport: CodeEngineImport | null;
  busy: "install" | "import" | "open" | null;
  /** Ferramentas das extensões (barra da esquerda), como na barra de atividades do VS Code. */
  tools: EngineTool[];
}

export const useCodeEngine = create<EngineState>(() => ({
  status: null,
  extensions: [],
  dialog: false,
  target: null,
  open: [],
  lastImport: null,
  busy: null,
  tools: [],
}));

let listening = false;
function listen() {
  const api = engine();
  if (listening || !api) return;
  listening = true;
  api.onStatus((status) => {
    const before = useCodeEngine.getState().status?.state;
    useCodeEngine.setState({ status });
    // Motor subiu (o registro das extensões foi conferido): a lista de ferramentas pode ter mudado.
    if (status.state === "running" && before !== "running") void refreshTools();
  });
  api.onClosed((projectId) => {
    bridges.get(projectId)?.stop();
    useCodeEngine.setState((s) => ({ open: s.open.filter((p) => p !== projectId) }));
  });
}

export async function refreshEngine() {
  const api = engine();
  if (!api) return;
  listen();
  const [status, extensions] = await Promise.all([api.status(), api.extensions()]);
  useCodeEngine.setState({ status, extensions });
  await refreshTools();
}

/** Lê as ferramentas das extensões habilitadas (instalar, habilitar ou desabilitar muda a lista). */
export async function refreshTools() {
  const api = engine();
  if (!api?.tools) return;
  listen();
  const tools = await api.tools().catch(() => [] as EngineTool[]);
  const before = JSON.stringify(useCodeEngine.getState().tools.map((t) => t.id));
  if (JSON.stringify(tools.map((t) => t.id)) !== before || !useCodeEngine.getState().tools.length)
    useCodeEngine.setState({ tools });
}

/**
 * Mostra a ferramenta de uma extensão no painel da esquerda (a tela das ferramentas do projeto).
 * Sem a ponte ainda (projeto recém-aberto), manda os arquivos para a pasta do motor primeiro.
 */
export async function embedTool(
  ws: WorkspaceContextValue,
  toolId: string,
): Promise<{ ok: boolean; error?: string; ready?: boolean }> {
  const api = engine();
  if (!api?.embedTool) return { ok: false, error: "Atualize o Kompil Desktop para usar as extensões." };
  listen();
  const projectId = ws.detail.project.id.toLowerCase();
  // A tela abre na hora, com a pasta do motor como estava; a ponte manda os arquivos logo em
  // seguida (ler o projeto inteiro antes deixava a ferramenta segundos em "Abrindo…").
  const res = await api.embedTool(projectId, {}, toolId);
  if (!res.ok) return res;
  if (!bridges.has(projectId)) {
    const bridge = new EngineBridge(ws, projectId);
    bridges.set(projectId, bridge);
    bridge.start();
  }
  useCodeEngine.setState((s) => ({ open: [...new Set([...s.open, projectId])] }));
  return res;
}

/** Motor disponível neste app (versões antigas do Kompil Desktop não têm). */
export const hasCodeEngine = () => engine() !== null;

const ASKED_KEY = "kompil.codeEngine.asked";

/** A pessoa já respondeu se quer trazer as extensões (não perguntamos de novo sozinhos). */
function markAsked() {
  try {
    localStorage.setItem(ASKED_KEY, "1");
  } catch {
    // sem armazenamento: pode perguntar de novo na próxima abertura
  }
}

const isInstalled = (s: CodeEngineStatus | null) =>
  Boolean(s && ["ready", "starting", "running", "importing"].includes(s.state));

/** Chave de uma extensão na lista de escolha (o mesmo id pode vir de dois editores). */
export const candidateKey = (c: Pick<CodeEngineCandidate, "source" | "id">) => `${c.source}:${c.id}`;

/** Por editor, o que fica de fora: as extensões dele que a pessoa desmarcou. */
export function exclusionsBySource(
  all: CodeEngineCandidate[],
  picked: ReadonlySet<string>,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const c of all) {
    const list = out.get(c.source) ?? [];
    if (!picked.has(candidateKey(c))) list.push(c.id);
    out.set(c.source, list);
  }
  return out;
}

const IMPORT_TOAST = "code-engine-import";
let importing: Promise<void> | null = null;

/** Aviso do sistema quando o Kompil não está na frente (a pessoa foi fazer outra coisa). */
function notifySystem(title: string, body: string) {
  if (document.hasFocus() || typeof Notification === "undefined") return;
  try {
    if (Notification.permission === "granted") new Notification(title, { body, silent: false });
  } catch {
    // sem notificação do sistema: o aviso do app basta
  }
}

/**
 * Importa em segundo plano as extensões escolhidas (e prepara o motor antes, se faltar). O
 * andamento fica numa notificação; a pessoa continua trabalhando e é avisada quando termina.
 */
export function importInBackground(
  all: CodeEngineCandidate[],
  picked: ReadonlySet<string>,
  after?: { label: string; run: () => void },
) {
  const api = engine();
  if (!api) return;
  if (importing) {
    toast("A importação das extensões já está em andamento.");
    return;
  }
  listen();
  const total = all.filter((c) => picked.has(candidateKey(c))).length;
  const background = "Pode continuar trabalhando: avisamos quando terminar.";
  toast.loading(`Importando ${total} extensões do VS Code…`, {
    id: IMPORT_TOAST,
    description: background,
    duration: Infinity,
  });
  const off = api.onStatus((st) => {
    if (st.state === "downloading")
      toast.loading("Preparando as extensões do VS Code…", {
        id: IMPORT_TOAST,
        description: `Baixando ${Math.round((st.progress ?? 0) * 100)}% · ${background}`,
        duration: Infinity,
      });
    else if (st.state === "importing" && st.message)
      toast.loading(`Importando ${total} extensões do VS Code…`, {
        id: IMPORT_TOAST,
        description: `${st.message} · ${background}`,
        duration: Infinity,
      });
  });
  useCodeEngine.setState({ busy: "import" });
  importing = (async () => {
    try {
      let status = await api.status();
      if (!isInstalled(status)) status = await api.install();
      if (!isInstalled(status))
        throw new Error(status.message ?? "Não deu para preparar as extensões do VS Code.");
      const report: CodeEngineImport = { copied: [], skipped: [], failed: [], limited: [] };
      for (const [source, exclude] of exclusionsBySource(all, picked)) {
        const r = await api.importExtensions(source as VsCodeInstall["source"], exclude);
        report.copied.push(...r.copied);
        report.skipped.push(...r.skipped);
        report.failed.push(...r.failed);
        report.limited.push(...r.limited);
      }
      useCodeEngine.setState({ lastImport: report });
      await refreshEngine();
      const ready = report.copied.length + report.skipped.length;
      const details = [
        `${ready} extensões do VS Code prontas.`,
        report.limited.length
          ? `${report.limited.length} da Microsoft podem não ativar fora do VS Code oficial.`
          : "",
        report.failed.length ? `${report.failed.length} não foram encontradas na pasta do editor.` : "",
        useCodeEngine.getState().open.length
          ? "Recarregue a janela do VS Code (Ctrl+R) para ativar as novas."
          : "",
      ]
        .filter(Boolean)
        .join(" ");
      toast.success("Importação concluída", {
        id: IMPORT_TOAST,
        description: details,
        duration: 12_000,
        ...(after ? { action: { label: after.label, onClick: after.run } } : {}),
      });
      notifySystem("Kompil · importação concluída", `${ready} extensões do VS Code prontas.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error("A importação das extensões parou", {
        id: IMPORT_TOAST,
        description: message,
        duration: 12_000,
      });
      notifySystem("Kompil · importação parou", message);
    } finally {
      off();
      importing = null;
      useCodeEngine.setState({ busy: null });
    }
  })();
}

/** Abre a lista para escolher as extensões (e, se vier de um projeto, abre nele no fim). */
export function chooseExtensions(target: WorkspaceContextValue | null = null) {
  useCodeEngine.setState({ dialog: true, target });
}

/**
 * Primeira abertura do app: o motor já veio instalado; se a pessoa tem extensões no VS Code (ou
 * Cursor, Windsurf…) e o motor ainda está vazio, pergunta uma vez se quer trazê-las.
 */
export function useFirstRunEnginePrompt(enabled: boolean) {
  useEffect(() => {
    const api = engine();
    if (!enabled || !api || !desktop) return;
    // Com a resposta guardada no app, vale ela (a marca antiga do navegador não conta).
    if (!api.vscodeConsent) {
      try {
        if (localStorage.getItem(ASKED_KEY)) return;
      } catch {
        return;
      }
    }
    let cancelled = false;
    void (async () => {
      const status = await api.status();
      if (cancelled || !isInstalled(status)) return;
      // Pergunta enquanto a pessoa não responder (a resposta fica no app, não só nesta tela).
      const consent = await api.vscodeConsent?.().catch(() => null);
      if (consent) {
        if (cancelled || consent.decision !== null || consent.available === 0) return;
      } else {
        // App antigo (sem a resposta guardada): só com o motor vazio.
        if (status.extensions > 0) return;
        const editors = await desktop!.vscode.detect().catch(() => []);
        if (cancelled || !editors.some((e) => e.extensions.length)) return;
      }
      useCodeEngine.setState({ status, dialog: true, target: null });
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);
}

// ───────────── Ponte: projeto do time ↔ pasta do motor ─────────────

const bridges = new Map<string, EngineBridge>();

class EngineBridge {
  private files = new Map<string, { path: string; handle: FileHandle; off: () => void }>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private offs: Array<() => void> = [];
  private warnedReadOnly = false;
  private stopped = false;

  constructor(
    private readonly ws: WorkspaceContextValue,
    readonly projectId: string,
  ) {}

  start() {
    const api = engine()!;
    const session = this.ws.session;
    this.reconcile();
    this.offs.push(session.on("tree", () => this.reconcile()));
    // Perdeu a permissão "Ver .env": o arquivo sai da pasta do motor.
    this.offs.push(
      session.on("secret", (fileId) => {
        const entry = this.files.get(fileId);
        if (!entry) return;
        entry.off();
        entry.handle.release();
        this.files.delete(fileId);
        clearTimeout(this.timers.get(fileId));
        this.send(entry.path, null);
      }),
    );
    this.offs.push(
      api.onFile((e) => {
        if (e.projectId === this.projectId) void this.fromEngine(e.path, e.content);
      }),
    );
  }

  /** Detalhe atual do projeto (o do contexto de quando a ponte nasceu pode estar velho). */
  private envDetail() {
    const current = activeWorkspace.current;
    return current?.detail.project.id === this.projectId ? current.detail : this.ws.detail;
  }

  /** O motor salvou (ou apagou) um arquivo: entra no projeto como edição normal. */
  private async fromEngine(path: string, content: string | null) {
    const session = this.ws.session;
    if (!session.canWrite) {
      if (!this.warnedReadOnly)
        toast.error(
          "Seu papel não permite editar este projeto: o que mudar na janela do VS Code fica só lá.",
        );
      this.warnedReadOnly = true;
      return;
    }
    // .env sem a permissão: o que mudou na pasta do motor não entra no projeto.
    if (this.envDetail().canSeeEnv === false && isSecretPath(path)) return;
    try {
      // O que o Claude (ou uma ferramenta do motor) mudou entra na revisão de "arquivos alterados".
      const before = await readFile(session, path).catch(() => null);
      if (before === content) return;
      recordBefore(this.projectId, "claude", path, before);
      if (content === null) deleteFile(session, path);
      else await writeFile(session, path, content);
      recordAfter(this.projectId, "claude", path, content);
    } catch (err) {
      toast.error(`Não deu para trazer ${path} do VS Code`, {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private send(path: string, content: string | null) {
    if (!this.stopped) engine()?.write(this.projectId, path, content);
  }

  /** Acompanha cada arquivo do projeto: o que o time muda vai para a pasta do motor. */
  private reconcile() {
    const tree = this.ws.session.tree();
    const alive = new Set<string>();
    // Sem a permissão "Ver .env", os .env nem vão para a pasta do motor (e saem de lá se estavam).
    const envAllowed = this.envDetail().canSeeEnv !== false;
    for (const node of tree.nodes.values()) {
      // Binários não têm texto para o motor (o Claude lê os de texto).
      if (node.type !== "file" || isBlobNode(node)) continue;
      const path = tree.paths.get(node.id);
      if (!path || (!envAllowed && isSecretPath(path))) continue;
      alive.add(node.id);
      const known = this.files.get(node.id);
      if (known) {
        if (known.path !== path) {
          // Renomeado ou movido.
          this.send(known.path, null);
          known.path = path;
          this.send(path, known.handle.text.toString());
        }
        continue;
      }
      const handle = this.ws.session.openFile(node.id);
      const entry = { path, handle, off: () => {} };
      const push = () => {
        clearTimeout(this.timers.get(node.id));
        this.timers.set(
          node.id,
          setTimeout(() => this.send(entry.path, handle.text.toString()), 250),
        );
      };
      handle.text.observe(push);
      entry.off = () => handle.text.unobserve(push);
      this.files.set(node.id, entry);
      void this.ws.session.loaded(handle).then(() => this.send(entry.path, handle.text.toString()));
    }
    for (const [id, entry] of [...this.files]) {
      if (alive.has(id)) continue;
      entry.off();
      entry.handle.release();
      this.files.delete(id);
      this.send(entry.path, null);
    }
  }

  stop() {
    this.stopped = true;
    for (const off of this.offs) off();
    for (const t of this.timers.values()) clearTimeout(t);
    for (const entry of this.files.values()) {
      entry.off();
      entry.handle.release();
    }
    this.files.clear();
    bridges.delete(this.projectId);
  }
}

const CLAUDE_EXTENSION = "anthropic.claude-code";

/**
 * O plugin Claude Code oficial no painel do Kompil. O motor VS Code nunca aparece como janela: ele
 * só hospeda o plugin, e a tela dele fica presa ao painel. Na primeira vez, prepara o motor e
 * instala o plugin; o projeto vai para a pasta do motor e o que o Claude mudar volta para o time.
 */
/** Motor e plugin do Claude já instalados? (o pré-carregamento não baixa nada sozinho) */
export async function claudeEngineInstalled(): Promise<boolean> {
  const api = engine();
  if (!api?.embedClaude) return false;
  const status = await api.status().catch(() => null);
  if (!isInstalled(status)) return false;
  return (await api.extensions().catch(() => [])).some((e) => e.id === CLAUDE_EXTENSION);
}

export async function embedClaude(
  ws: WorkspaceContextValue,
  request: ClaudeEmbedRequest = {},
): Promise<{ ok: boolean; error?: string; ready?: boolean }> {
  const api = engine();
  if (!api?.embedClaude) return { ok: false, error: "Atualize o Kompil Desktop para usar o Claude Code." };
  listen();
  const projectId = ws.detail.project.id.toLowerCase();
  // Já aberto e sincronizando: só o pedido, sem conferir motor/plugin nem reenviar o projeto.
  if (bridges.has(projectId) && (await api.embedState?.(projectId))?.open)
    return api.embedClaude(projectId, {}, request);
  let status = await api.status();
  useCodeEngine.setState({ status });
  if (status.state === "unsupported")
    return { ok: false, error: "O Claude Code ainda não funciona neste sistema." };
  if (!isInstalled(status)) {
    status = await api.install();
    if (!isInstalled(status))
      return { ok: false, error: status.message ?? "Não deu para preparar o Claude Code." };
  }
  if (!(await api.extensions()).some((e) => e.id === CLAUDE_EXTENSION)) {
    const res = await api.installExtension(CLAUDE_EXTENSION);
    if (!res.ok) return { ok: false, error: res.error ?? "Não deu para instalar o plugin Claude Code." };
    await refreshEngine();
  }
  const files = await readProjectTexts(ws.session);
  const res = await api.embedClaude(projectId, files, request);
  if (!res.ok) return res;
  if (!bridges.has(projectId)) {
    const bridge = new EngineBridge(ws, projectId);
    bridges.set(projectId, bridge);
    bridge.start();
  }
  useCodeEngine.setState((s) => ({ open: [...new Set([...s.open, projectId])] }));
  return res;
}

/** Projeto fechado no Kompil: a ponte para e o Claude embutido fecha junto (sem ponte, não há sync). */
export function useCloseEngineWith(ws: WorkspaceContextValue) {
  const projectId = ws.detail.project.id.toLowerCase();
  useEffect(
    () => () => {
      if (!bridges.has(projectId)) return;
      bridges.get(projectId)?.stop();
      engine()?.close(projectId);
    },
    [projectId],
  );
}

// ───────────── Interface ─────────────

const STATE_LABEL: Record<CodeEngineStatus["state"], string> = {
  missing: "Não instalado",
  downloading: "Baixando",
  verifying: "Conferindo o pacote",
  extracting: "Extraindo",
  importing: "Trazendo extensões",
  ready: "Pronto",
  starting: "Iniciando",
  running: "Rodando",
  error: "Erro",
  unsupported: "Indisponível neste sistema",
};

function Progress({ status }: { status: CodeEngineStatus }) {
  const measured = status.state === "downloading" || status.state === "importing";
  const pct = measured ? Math.round((status.progress ?? 0) * 100) : null;
  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-[12px] text-fg-muted">
        <span className="flex min-w-0 items-center gap-1.5">
          <Spinner className="h-3 w-3 shrink-0" /> {STATE_LABEL[status.state]}
          {status.state === "importing" && status.message ? (
            <span className="truncate text-fg-subtle">· {status.message}</span>
          ) : null}
        </span>
        {pct !== null ? <span className="shrink-0">{pct}%</span> : null}
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-bg-emphasis">
        <div
          className={cn("h-full rounded-full bg-fg transition-[width]", pct === null && "animate-pulse")}
          style={{ width: `${pct ?? 100}%`, opacity: pct === null ? 0.5 : 1 }}
        />
      </div>
    </div>
  );
}

/**
 * Trazer as extensões do VS Code: a lista de todas (marcadas), para a pessoa tirar as que não
 * quer. A importação roda em segundo plano, com o andamento numa notificação.
 */
export function CodeEngineDialog() {
  const dialog = useCodeEngine((s) => s.dialog);
  if (!dialog) return null;
  return <ExtensionPicker />;
}

/**
 * As extensões para escolher. App desktop de antes da lista (sem `candidates`): monta pelos
 * editores detectados, só com os ids.
 */
async function loadCandidates(): Promise<CodeEngineCandidate[]> {
  const api = engine();
  if (!api || !desktop) return [];
  if (typeof api.candidates === "function") return api.candidates();
  const editors = await desktop.vscode.detect();
  return editors.flatMap((e) =>
    e.extensions.map((id) => ({ source: e.source, id, version: "", linked: false })),
  );
}

function ExtensionPicker() {
  const { status } = useCodeEngine();
  const [list, setList] = useState<CodeEngineCandidate[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  useEffect(() => {
    let alive = true;
    void loadCandidates()
      .catch(() => [] as CodeEngineCandidate[])
      .then((all) => {
        if (!alive) return;
        setList(all);
        setPicked(new Set(all.map(candidateKey)));
      });
    return () => {
      alive = false;
    };
  }, []);
  const close = (declined = false) => {
    markAsked();
    useCodeEngine.setState({ dialog: false, target: null });
    // Recusou na pergunta da primeira vez: nada do VS Code vem para o Kompil (e não pergunta de novo).
    if (declined)
      void engine()
        ?.vscodeConsent?.()
        .then((c) => (c?.decision === null ? engine()?.declineVscode?.() : undefined))
        .then(() => refreshEngine())
        .catch(() => null);
  };
  const term = q.trim().toLowerCase();
  const shown = (list ?? []).filter(
    (c) => !term || `${c.name ?? ""} ${c.id} ${c.publisher ?? ""}`.toLowerCase().includes(term),
  );
  const sources = new Set((list ?? []).map((c) => c.source));
  const download = !isInstalled(status) && !status?.bundled;
  const count = (list ?? []).filter((c) => picked.has(candidateKey(c))).length;
  const toggle = (key: string, on: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  const setAll = (on: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      for (const c of shown) {
        if (on) next.add(candidateKey(c));
        else next.delete(candidateKey(c));
      }
      return next;
    });
  return (
    <Dialog open onOpenChange={(o) => !o && close(true)}>
      <DialogContent
        wide
        title="Trazer suas extensões do VS Code para o Kompil?"
        description="Encontramos as extensões instaladas no seu VS Code. Com a sua autorização, elas passam a funcionar no Kompil como no próprio VS Code — nada é copiado e o que você atualizar lá vale aqui. Todas vêm marcadas: desmarque as que não quiser."
      >
        {list === null ? (
          <p className="flex items-center gap-2 py-6 text-[13px] text-fg-muted">
            <Spinner className="h-3.5 w-3.5" /> Procurando as extensões deste computador…
          </p>
        ) : list.length === 0 ? (
          <p className="py-6 text-[13px] text-fg-muted">
            Não encontramos extensões do VS Code (nem do Cursor, Windsurf ou VSCodium) neste computador.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={`Buscar entre ${list.length} extensões`}
                  aria-label="Buscar extensão"
                  className="h-8 w-full rounded-md border border-border bg-bg pl-8 pr-2 text-[12.5px] text-fg placeholder:text-fg-subtle focus:border-fg-subtle focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={() => setAll(true)}
                className="text-[12px] text-fg-muted hover:text-fg"
              >
                Marcar todas
              </button>
              <button
                type="button"
                onClick={() => setAll(false)}
                className="text-[12px] text-fg-muted hover:text-fg"
              >
                Desmarcar todas
              </button>
            </div>
            <ul className="max-h-[46vh] overflow-y-auto rounded-md border border-border">
              {shown.map((c) => {
                const key = candidateKey(c);
                return (
                  <li key={key} className="border-b border-border last:border-0">
                    <label className="flex cursor-pointer items-start gap-2.5 px-3 py-2 hover:bg-bg-muted">
                      <input
                        type="checkbox"
                        checked={picked.has(key)}
                        onChange={(e) => toggle(key, e.target.checked)}
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[var(--fg)]"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-[13px] font-medium text-fg">{c.name ?? c.id}</span>
                          {c.warning ? (
                            <Tip label={c.warning}>
                              <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-[var(--warning,#d4a72c)]" />
                            </Tip>
                          ) : null}
                          {c.linked ? (
                            <span className="shrink-0 rounded bg-bg-emphasis px-1 text-[10.5px] text-fg-muted">
                              instalada
                            </span>
                          ) : null}
                        </span>
                        <span className="block truncate text-[11.5px] text-fg-subtle">
                          {c.id}
                          {c.version ? ` · ${c.version}` : ""}
                          {sources.size > 1 ? ` · ${SOURCE_LABEL[c.source] ?? c.source}` : ""}
                        </span>
                        {c.description ? (
                          <span className="block truncate text-[11.5px] text-fg-muted">{c.description}</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
              {!shown.length ? (
                <li className="px-3 py-4 text-center text-[12px] text-fg-subtle">
                  Nenhuma extensão encontrada.
                </li>
              ) : null}
            </ul>
            <p className="flex gap-1.5 text-[12px] leading-relaxed text-fg-subtle">
              <Bell className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Você não precisa acompanhar: a importação roda em segundo plano, com o andamento nas
              notificações, e avisamos quando terminar.
              {download ? ` Na primeira vez, baixa cerca de ${CODE_ENGINE.sizeMb} MB.` : ""}
            </p>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => close(true)}>
            Agora não
          </Button>
          <Button
            variant="primary"
            disabled={!list?.length}
            onClick={() => {
              const all = list ?? [];
              close();
              importInBackground(all, picked);
            }}
          >
            <Download className="h-3.5 w-3.5" /> {count ? `Trazer ${count} extensões` : "Tirar todas"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  vscode: "VS Code",
  "vscode-insiders": "VS Code Insiders",
  cursor: "Cursor",
  vscodium: "VSCodium",
  windsurf: "Windsurf",
};

/** Cartão do painel Extensões: o motor (sem interface própria) e as extensões dele. */
export function CodeEngineCard({ ws }: { ws: WorkspaceContextValue | null }) {
  const { status, extensions, busy } = useCodeEngine();
  useEffect(() => {
    void refreshEngine();
  }, []);
  if (!hasCodeEngine()) return null;
  const installed = isInstalled(status);
  const limited = extensions.filter((e) => e.warning).length;
  return (
    <div className="mx-3 mb-2 rounded-lg border border-border bg-bg-subtle p-3 text-[12.5px]">
      <p className="flex items-center gap-1.5 font-medium text-fg">
        <Blocks className="h-3.5 w-3.5" /> Extensões do VS Code
        <span className="ml-auto text-[11px] font-normal text-fg-subtle">
          {status ? STATE_LABEL[status.state] : "…"}
        </span>
      </p>
      <p className="mt-1 text-[12px] leading-snug text-fg-muted">
        {installed
          ? `${extensions.length} extensões do VS Code instaladas${limited ? ` (${limited} da Microsoft podem não ativar)` : ""}.`
          : "Use as suas extensões do VS Code neste projeto."}
      </p>
      {status && ["downloading", "verifying", "extracting", "importing"].includes(status.state) ? (
        <div className="mt-2">
          <Progress status={status} />
        </div>
      ) : null}
      <div className="mt-2.5 flex flex-col gap-1.5">
        {installed ? (
          <Button
            size="sm"
            variant="secondary"
            className="w-full justify-center"
            loading={busy === "import"}
            onClick={() => chooseExtensions(ws)}
          >
            <Puzzle className="h-3.5 w-3.5" /> Escolher do VS Code
          </Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => chooseExtensions(ws)}>
            <Download className="h-3.5 w-3.5" /> Instalar
          </Button>
        )}
      </div>
    </div>
  );
}
